// Libraries
import React from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

// Components
import { ModalV2, SIZES } from 'src/components/HTKit/Modals/ModalV2';
import Button, { THEMES } from 'src/components/HTKit/Forms/Button';
import Grid from 'src/components/HTKit/Grid/Grid';
import { Picture } from 'src/components/Elements/Picture';
import HTGladlyChat from 'src/utils/Gladly/HTGladlyChat';

// Images, constants and styles
import { specialistChatClickSegment } from 'src/containers/ProductPage/actions';
import { MODAL_CONTENTS, calendlyLink } from './constants';
import styles from './styles.scss';

const CtaButton = ({ onClick, text, theme }) => (
  <Button theme={theme} className={styles.button} onClick={onClick}>
    {text}
  </Button>
);

const ProductSpecialistModal = ({ visible, onCancel, service }) => {
  const dispatchReduxAction = useDispatch();
  const timeNow = new Date().getUTCHours() - 7; // PST is 7 hours behind UTC
  const isBusinessHours = timeNow >= 9 && timeNow <= 17 ? 'showChatContent' : 'dontShowChatContent';
  const showBusinessHoursContent = isBusinessHours === 'showChatContent';

  const handleChatButtonClick = () => {
    dispatchReduxAction(
      specialistChatClickSegment({ service, location: 'Modal', buttonClicked: 'Chat Now' }),
    );
    HTGladlyChat.open();
    onCancel();
  };

  const handleScheduleButtonClick = () => {
    dispatchReduxAction(
      specialistChatClickSegment({ service, location: 'Modal', buttonClicked: 'Schedule a Call' }),
    );
    window.open(calendlyLink, '_blank');
    onCancel();
  };

  const imageStyles = cn('paddingBottom-small2', styles.specialistImage, {
    [styles.offHoursSpecialistImage]: !showBusinessHoursContent,
  });

  return (
    <ModalV2
      isVisible={visible}
      hide={onCancel}
      size={SIZES.MEDIUM}
      header={MODAL_CONTENTS.modalHeader[isBusinessHours]}
      footerElement2={
        <CtaButton
          text="Schedule a Call"
          theme={MODAL_CONTENTS.modalButtonTheme[isBusinessHours]}
          onClick={handleScheduleButtonClick}
        />
      }
      footerElement3={
        showBusinessHoursContent && (
          <CtaButton text="Chat now" theme={THEMES.V2PRIMARY} onClick={handleChatButtonClick} />
        )
      }
    >
      <Grid.FullWidth>
        {showBusinessHoursContent ? (
          <Grid.Row>
            <Grid.Column sm={4} md={8} lg={12}>
              <Picture
                sources={MODAL_CONTENTS.imageSources[isBusinessHours]}
                className={imageStyles}
                alt="product-specialist"
              />
            </Grid.Column>
            <Grid.Column sm={4} md={8} lg={12}>
              <p className="p0 n900">{MODAL_CONTENTS.modalBodyText[isBusinessHours]}</p>
            </Grid.Column>
          </Grid.Row>
        ) : (
          <Grid.Row>
            <Grid.Column sm={4} md={8} lg={12}>
              <p className="p0 n900">{MODAL_CONTENTS.modalBodyText[isBusinessHours]}</p>
              <p className="p0 n900 paddingTop-small2">
                {MODAL_CONTENTS.modalBodyTextSecondLine[isBusinessHours]}
              </p>
            </Grid.Column>
            <Grid.Column sm={4} md={8} lg={12}>
              <Picture
                sources={MODAL_CONTENTS.imageSources[isBusinessHours]}
                className={imageStyles}
                alt="product-specialist"
              />
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid.FullWidth>
    </ModalV2>
  );
};

ProductSpecialistModal.propTypes = {
  onSpecialistChatClick: PropTypes.func,
  service: PropTypes.object,
  visible: PropTypes.bool,
  onCancel: PropTypes.func,
};

CtaButton.propTypes = {
  onClick: PropTypes.func,
  text: PropTypes.string,
  theme: PropTypes.string,
};

export default ProductSpecialistModal;
